import React, { useMemo, useState } from 'react';
import { Filter, Search, Trash2, X } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { ListSortMenu, type ListSortOption } from '@/shared/components/ListSortMenu';
import { Label } from '@/shared/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/custom/tabs';
import { toast } from '@/shared/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  formatUnitLabel,
  type AreaTagSetMinimal,
  type LocationMinimal,
  type PunchListFilterView,
  type PunchListItemFilters,
  type PunchListItemType,
  type PunchListPriority,
  type PunchListsSavedSortState,
  type PunchListStatus,
  type PunchListsSortState,
  type Trade,
  type UserMinimal,
} from '../types';
import {
  useCreatePunchListFilterView,
  useDeletePunchListFilterView,
  usePunchListFilterViews,
} from '../hooks';
import { normalizePunchListsSortState, serializePunchListsSortState } from '../utils/sortState';

interface Props {
  projectId: number | undefined;
  filters: PunchListItemFilters;
  sort: PunchListsSortState[];
  onFiltersChange: (filters: PunchListItemFilters) => void;
  onFilterViewApply: (view: PunchListFilterView) => void;
  assignees: UserMinimal[];
  trades: Trade[];
  itemTypes: PunchListItemType[];
  locations: LocationMinimal[];
  areaTagSets: AreaTagSetMinimal[];
  selectedCount: number;
  selectableItemCount: number;
  allSelectableSelected: boolean;
  partiallySelected: boolean;
  onSelectAll: (checked: boolean) => void;
  onSortChange: (sort: PunchListsSortState[]) => void;
}

const SORT_OPTIONS: readonly ListSortOption<PunchListsSortState['key']>[] = [
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'item_type', label: 'Type' },
  { key: 'location', label: 'Location' },
  { key: 'due_date', label: 'Due date' },
  { key: 'created_at', label: 'Created' },
];

export const PunchListsFilters: React.FC<Props> = ({
  projectId,
  filters,
  sort,
  onFiltersChange,
  onFilterViewApply,
  assignees,
  trades,
  itemTypes,
  locations,
  areaTagSets,
  selectedCount,
  selectableItemCount,
  allSelectableSelected,
  partiallySelected,
  onSelectAll,
  onSortChange,
}) => {
  const [viewName, setViewName] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const { data: filterViews, isLoading: filterViewsLoading } = usePunchListFilterViews(projectId);
  const createFilterView = useCreatePunchListFilterView(projectId);
  const deleteFilterView = useDeletePunchListFilterView(projectId);

  const activeFilterCount = useMemo(
    () =>
      [
        filters.search,
        filters.status,
        filters.priority,
        filters.assignee,
        filters.assignee_unassigned,
        filters.trade_id,
        filters.item_type_id,
        filters.area_tag_id || filters.location_id,
        filters.area_tag_set_id,
        filters.due_state,
        filters.visibility,
        filters.hotspot_state,
        filters.due_from,
        filters.due_to,
        filters.created_from,
        filters.created_to,
      ].filter(Boolean).length,
    [filters]
  );
  const hasActiveFilters = useMemo(() => activeFilterCount > 0, [activeFilterCount]);
  const filterReviewItems = useMemo(
    () =>
      buildFilterReviewItems(filters, sort, assignees, trades, itemTypes, locations, areaTagSets),
    [areaTagSets, assignees, filters, itemTypes, locations, sort, trades]
  );

  const update = (patch: Partial<PunchListItemFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  const setSelectValue = <K extends keyof PunchListItemFilters>(
    key: K,
    value: PunchListItemFilters[K] | undefined
  ) => update({ [key]: value } as Partial<PunchListItemFilters>);

  const handleSaveFilterView = async () => {
    if (!projectId) return;

    const name = viewName.trim();
    if (!name) {
      toast({ title: 'Name your filter view first.' });
      return;
    }

    try {
      await createFilterView.mutateAsync({
        name,
        project: projectId,
        filters: cleanFilterPayload(filters),
        sort_state: serializePunchListsSortState(sort),
      });
      setViewName('');
      setSaveDialogOpen(false);
      toast({ title: 'Filter view saved.' });
    } catch {
      toast({ title: 'Failed to save filter view.', variant: 'destructive' });
    }
  };

  const handleDeleteFilterView = async (id: string) => {
    try {
      await deleteFilterView.mutateAsync(id);
      toast({ title: 'Filter view deleted.' });
    } catch {
      toast({ title: 'Failed to delete filter view.', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-3" data-tour-id="punch-lists-filters">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 md:max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search punch items…"
            value={filters.search ?? ''}
            onChange={(e) => update({ search: e.target.value || undefined })}
            className="w-full pl-8"
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="hidden gap-2 md:inline-flex"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>

        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="hidden md:inline-flex"
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFiltersChange({})}
            className="hidden gap-1 md:inline-flex"
          >
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        )}
      </div>

      <div className="flex min-w-0 items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 md:hidden">
        <label
          htmlFor="punch-list-select-all-mobile"
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm"
        >
          <Checkbox
            id="punch-list-select-all-mobile"
            aria-label="Select all filtered punch list items"
            checked={allSelectableSelected ? true : partiallySelected ? 'indeterminate' : false}
            disabled={selectableItemCount === 0}
            onCheckedChange={(checked) => onSelectAll(checked === true)}
          />
          <span className="truncate">
            {selectedCount > 0 ? `${selectedCount} selected` : 'Select all'}
          </span>
        </label>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 gap-2 px-2.5"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>

        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="shrink-0 px-2.5"
        />
      </div>

      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="max-w-2xl" aria-describedby="punch-list-filter-description">
          <DialogHeader>
            <DialogTitle>Filters</DialogTitle>
            <DialogDescription id="punch-list-filter-description" className="sr-only">
              Refine the punch list table.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="filters" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="filters">Filters</TabsTrigger>
              <TabsTrigger value="filter-views">Filter Views</TabsTrigger>
            </TabsList>

            <TabsContent value="filters" className="mt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <FilterSelect label="Status">
                  <Select
                    value={filters.status ?? 'all'}
                    onValueChange={(value) =>
                      setSelectValue(
                        'status',
                        value === 'all' ? undefined : (value as PunchListStatus)
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {(Object.keys(STATUS_LABELS) as PunchListStatus[]).map((status) => (
                        <SelectItem key={status} value={status}>
                          {STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Priority">
                  <Select
                    value={filters.priority ?? 'all'}
                    onValueChange={(value) =>
                      setSelectValue(
                        'priority',
                        value === 'all' ? undefined : (value as PunchListPriority)
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All priorities</SelectItem>
                      {(Object.keys(PRIORITY_LABELS) as PunchListPriority[]).map((priority) => (
                        <SelectItem key={priority} value={priority}>
                          {PRIORITY_LABELS[priority]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Assignee">
                  <Select
                    value={
                      filters.assignee_unassigned
                        ? 'unassigned'
                        : filters.assignee
                          ? String(filters.assignee)
                          : 'all'
                    }
                    onValueChange={(value) =>
                      update({
                        assignee:
                          value === 'all' || value === 'unassigned' ? undefined : Number(value),
                        assignee_unassigned: value === 'unassigned' || undefined,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All assignees" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All assignees</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {assignees.map((assignee) => (
                        <SelectItem key={assignee.id} value={String(assignee.id)}>
                          {assignee.first_name} {assignee.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Type">
                  <Select
                    value={filters.item_type_id ?? 'all'}
                    onValueChange={(value) =>
                      setSelectValue('item_type_id', value === 'all' ? undefined : value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {itemTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Trade">
                  <Select
                    value={filters.trade_id ?? 'all'}
                    onValueChange={(value) =>
                      setSelectValue('trade_id', value === 'all' ? undefined : value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All trades" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All trades</SelectItem>
                      {trades.map((trade) => (
                        <SelectItem key={trade.id} value={trade.id}>
                          {trade.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Unit/Location">
                  <Select
                    value={
                      filters.area_tag_id
                        ? String(filters.area_tag_id)
                        : filters.location_id
                          ? String(filters.location_id)
                          : 'all'
                    }
                    onValueChange={(value) =>
                      update({
                        area_tag_id: value === 'all' ? undefined : Number(value),
                        location_id: undefined,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All units/locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All units/locations</SelectItem>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={String(location.id)}>
                          {formatUnitLabel(location)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Phase/Zone">
                  <Select
                    value={filters.area_tag_set_id ? String(filters.area_tag_set_id) : 'all'}
                    onValueChange={(value) =>
                      setSelectValue('area_tag_set_id', value === 'all' ? undefined : Number(value))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All phases/zones" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All phases/zones</SelectItem>
                      {areaTagSets.map((areaTagSet) => (
                        <SelectItem key={areaTagSet.id} value={String(areaTagSet.id)}>
                          {areaTagSet.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Due Date">
                  <Select
                    value={filters.due_state ?? 'all'}
                    onValueChange={(value) =>
                      setSelectValue(
                        'due_state',
                        value === 'all' ? undefined : (value as PunchListItemFilters['due_state'])
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any due date" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any due date</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="due_today">Due today</SelectItem>
                      <SelectItem value="due_next_7">Due next 7 days</SelectItem>
                      <SelectItem value="no_due_date">No due date</SelectItem>
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Visibility">
                  <Select
                    value={filters.visibility ?? 'all'}
                    onValueChange={(value) =>
                      setSelectValue(
                        'visibility',
                        value === 'all' ? undefined : (value as PunchListItemFilters['visibility'])
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All visibility</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="private">Private</SelectItem>
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <FilterSelect label="Hotspot">
                  <Select
                    value={filters.hotspot_state ?? 'all'}
                    onValueChange={(value) =>
                      setSelectValue(
                        'hotspot_state',
                        value === 'all'
                          ? undefined
                          : (value as PunchListItemFilters['hotspot_state'])
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All punch items" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All punch items</SelectItem>
                      <SelectItem value="linked">Linked to hotspot</SelectItem>
                      <SelectItem value="unlinked">No hotspot</SelectItem>
                    </SelectContent>
                  </Select>
                </FilterSelect>

                <DateInput
                  label="Due From"
                  value={filters.due_from}
                  onChange={(value) => setSelectValue('due_from', value)}
                />
                <DateInput
                  label="Due To"
                  value={filters.due_to}
                  onChange={(value) => setSelectValue('due_to', value)}
                />
                <DateInput
                  label="Created From"
                  value={filters.created_from}
                  onChange={(value) => setSelectValue('created_from', value)}
                />
                <DateInput
                  label="Created To"
                  value={filters.created_to}
                  onChange={(value) => setSelectValue('created_to', value)}
                />
              </div>
            </TabsContent>

            <TabsContent value="filter-views" className="mt-4">
              {filterViewsLoading ? (
                <div className="rounded-md border p-4 text-sm text-muted-foreground">
                  Loading filter views...
                </div>
              ) : (filterViews ?? []).length === 0 ? (
                <div className="rounded-md border p-4 text-sm text-muted-foreground">
                  No saved filter views yet.
                </div>
              ) : (
                <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                  {(filterViews ?? []).map((view) => (
                    <div
                      key={view.id}
                      className="flex items-center justify-between gap-3 rounded-md border p-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{view.name}</p>
                        <FilterViewSummary
                          items={buildFilterReviewItems(
                            view.filters ?? {},
                            view.sort_state,
                            assignees,
                            trades,
                            itemTypes,
                            locations,
                            areaTagSets
                          )}
                        />
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button variant="outline" size="sm" onClick={() => onFilterViewApply(view)}>
                          Apply
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => void handleDeleteFilterView(view.id)}
                          disabled={deleteFilterView.isPending}
                          title="Delete filter view"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="items-end gap-2 sm:space-x-0">
            <Button onClick={() => setSaveDialogOpen(true)} disabled={!projectId}>
              Save as Filter View
            </Button>
            <Button
              variant="outline"
              onClick={() => onFiltersChange({})}
              disabled={!hasActiveFilters}
            >
              Clear All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="save-filter-view-description">
          <DialogHeader>
            <DialogTitle>Save Filter View</DialogTitle>
            <DialogDescription id="save-filter-view-description" className="sr-only">
              Name this filter view and review the filters before saving.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="save-filter-view-name">Filter View Name</Label>
              <Input
                id="save-filter-view-name"
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                placeholder="My open items"
              />
            </div>

            <div className="space-y-2">
              <Label>Review</Label>
              {filterReviewItems.length === 0 ? (
                <div className="rounded-md border p-3 text-sm text-muted-foreground">
                  No active filters or sort.
                </div>
              ) : (
                <div className="flex max-h-[220px] flex-wrap gap-2 overflow-y-auto rounded-md border p-3">
                  {filterReviewItems.map((item) => (
                    <Badge key={`${item.label}-${item.value}`} variant="secondary">
                      {item.label}: {item.value}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSaveFilterView()}
              disabled={!projectId || createFilterView.isPending}
            >
              Save Filter View
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onFiltersChange({})}
          className="w-full gap-1 md:hidden"
        >
          <X className="h-4 w-4" />
          Clear Filters
        </Button>
      )}
    </div>
  );
};

function buildFilterReviewItems(
  filters: PunchListItemFilters,
  sort: PunchListsSavedSortState | readonly PunchListsSortState[] | null,
  assignees: UserMinimal[],
  trades: Trade[],
  itemTypes: PunchListItemType[],
  locations: LocationMinimal[],
  areaTagSets: AreaTagSetMinimal[]
) {
  const items: Array<{ label: string; value: string }> = [];
  const assignee = filters.assignee ? assignees.find((item) => item.id === filters.assignee) : null;
  const itemType = filters.item_type_id
    ? itemTypes.find((item) => item.id === filters.item_type_id)
    : null;
  const trade = filters.trade_id ? trades.find((item) => item.id === filters.trade_id) : null;
  const areaTagId = filters.area_tag_id ?? filters.location_id;
  const location = areaTagId ? locations.find((item) => item.id === areaTagId) : null;
  const areaTagSet = filters.area_tag_set_id
    ? areaTagSets.find((item) => item.id === filters.area_tag_set_id)
    : null;

  if (filters.search) items.push({ label: 'Search', value: filters.search });
  if (filters.status) items.push({ label: 'Status', value: STATUS_LABELS[filters.status] });
  if (filters.priority) items.push({ label: 'Priority', value: PRIORITY_LABELS[filters.priority] });
  if (filters.assignee_unassigned) {
    items.push({ label: 'Assignee', value: 'Unassigned' });
  } else if (filters.assignee) {
    items.push({
      label: 'Assignee',
      value: assignee ? `${assignee.first_name} ${assignee.last_name}` : String(filters.assignee),
    });
  }
  if (filters.item_type_id) {
    items.push({ label: 'Type', value: itemType?.name ?? filters.item_type_id });
  }
  if (filters.trade_id) {
    items.push({ label: 'Trade', value: trade?.name ?? filters.trade_id });
  }
  if (areaTagId) {
    items.push({
      label: 'Unit/Location',
      value: location ? formatUnitLabel(location) : String(areaTagId),
    });
  }
  if (filters.area_tag_set_id) {
    items.push({
      label: 'Phase/Zone',
      value: areaTagSet?.name ?? String(filters.area_tag_set_id),
    });
  }
  if (filters.due_state) {
    items.push({ label: 'Due Date', value: DUE_STATE_LABELS[filters.due_state] });
  }
  if (filters.visibility) {
    items.push({ label: 'Visibility', value: VISIBILITY_LABELS[filters.visibility] });
  }
  if (filters.hotspot_state) {
    items.push({ label: 'Hotspot', value: HOTSPOT_STATE_LABELS[filters.hotspot_state] });
  }
  if (filters.due_from) items.push({ label: 'Due From', value: filters.due_from });
  if (filters.due_to) items.push({ label: 'Due To', value: filters.due_to });
  if (filters.created_from) items.push({ label: 'Created From', value: filters.created_from });
  if (filters.created_to) items.push({ label: 'Created To', value: filters.created_to });
  normalizePunchListsSortState(sort).forEach((rule, index) => {
    items.push({
      label: `Sort ${index + 1}`,
      value: `${SORT_LABELS[rule.key]} ${rule.direction === 'asc' ? 'ascending' : 'descending'}`,
    });
  });

  return items;
}

function FilterViewSummary({ items }: { items: Array<{ label: string; value: string }> }) {
  if (items.length === 0) {
    return <p className="mt-2 text-xs text-muted-foreground">Filters: none</p>;
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">Filters:</span>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <Badge
            key={`${item.label}-${item.value}`}
            variant="secondary"
            className="max-w-[180px] truncate text-[11px] font-medium"
            title={`${item.label}: ${item.value}`}
          >
            {item.label}: {item.value}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function cleanFilterPayload(filters: PunchListItemFilters): PunchListItemFilters {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== '')
  ) as PunchListItemFilters;
}

const DUE_STATE_LABELS: Record<NonNullable<PunchListItemFilters['due_state']>, string> = {
  overdue: 'Overdue',
  due_today: 'Due today',
  due_next_7: 'Due next 7 days',
  no_due_date: 'No due date',
};

const VISIBILITY_LABELS: Record<NonNullable<PunchListItemFilters['visibility']>, string> = {
  private: 'Private',
  shared: 'Shared',
};

const HOTSPOT_STATE_LABELS: Record<NonNullable<PunchListItemFilters['hotspot_state']>, string> = {
  linked: 'Linked to hotspot',
  unlinked: 'No hotspot',
};

const SORT_LABELS: Record<PunchListsSortState['key'], string> = {
  status: 'Status',
  priority: 'Priority',
  item_type: 'Type',
  location: 'Location',
  due_date: 'Due Date',
  created_at: 'Created',
};

function FilterSelect({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type="date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    </div>
  );
}

export default PunchListsFilters;
